/**
 * The selection, drawn once.
 *
 * One absolutely-positioned rectangle over the rows, rather than a border on
 * each selected cell. That is the difference between one element changing when
 * the selection moves and every cell on screen re-rendering to recompute a
 * modifier class it mostly does not have.
 *
 * It sits inside the same spacer the rows do, so it scrolls with them and needs
 * no synchronisation. `pointer-events: none` because it covers the cells it
 * describes, and a rectangle that swallowed clicks would make the selection
 * impossible to move with the mouse.
 */
import { useStore } from 'zustand'

import type { RowWindow } from '../rows/rowWindow'
import { columnLayout, type GridColumn } from './columns'
import { rectFor } from './selection'
import type { SelectionStore } from './selection-store'

export function SelectionOverlay({
  store,
  window,
  columns,
  rowHeight,
  gutter,
}: {
  store: SelectionStore
  window: RowWindow
  columns: readonly GridColumn[]
  rowHeight: number
  gutter: number
}) {
  const range = useStore(store, (state) => state.range)
  if (range === null) return null

  const rect = rectFor(range, window, columns)
  if (rect === null) return null

  const { offsets } = columnLayout(columns, gutter)
  const left = offsets[rect.left]
  if (left === undefined) return null

  const width = columns
    .slice(rect.left, rect.left + rect.columns)
    .reduce((total, column) => total + column.width, 0)

  return (
    <div
      // Decorative: what is selected is said by `aria-activedescendant` on the
      // grid, which names a real cell. A second announcement of the same fact
      // would be read out twice.
      aria-hidden
      data-testid="selection"
      className="pointer-events-none absolute z-10 rounded-[2px] ring-2 ring-primary ring-inset"
      style={{
        top: rect.top * rowHeight,
        left,
        width,
        height: rect.rows * rowHeight,
      }}
    />
  )
}
